/**
 * Editing a role or a channel that someone else may change at the same time.
 *
 * An editor keeps the object as it was when the edit began (`base`) and the
 * person's draft. When the live object changes, the draft is rebased onto it
 * (a three-way merge): what the person did not touch follows the live
 * object, what they changed stays theirs. Permission bits merge bit by bit,
 * channel overwrites target by target. Saving sends only what differs from
 * `base`, so it never undoes a change made elsewhere.
 */
import {
  parsePermissions,
  serializePermissions,
  type Overwrite,
  type Role,
  type RolePokeRule,
  type Snowflake,
} from '@neosocial/protocol';

/** Bits the draft changed from the base keep the draft's value; the rest follow live. */
export function mergeBits(draft: bigint, base: bigint, live: bigint): bigint {
  const touched = draft ^ base;
  return (draft & touched) | (live & ~touched);
}

/* ------------------------------------------------------------------ roles */

export interface RoleFields {
  name: string;
  color: number;
  hoist: boolean;
  mentionable: boolean;
  perms: bigint;
  nx: bigint;
  /** Who may poke members with the role (`nx_pokes_from`, 2026-10-29). */
  pokesFrom: RolePokeRule;
}

export function roleFields(r: Role | null | undefined): RoleFields {
  return {
    name: r?.name ?? '',
    color: r?.color ?? 0,
    hoist: r?.hoist ?? false,
    mentionable: r?.mentionable ?? false,
    perms: parsePermissions(r?.permissions ?? '0'),
    nx: parsePermissions(r?.nx_permissions ?? '0'),
    pokesFrom: r?.nx_pokes_from ?? 'everyone',
  };
}

export function sameRole(a: RoleFields, b: RoleFields): boolean {
  return (
    a.name === b.name &&
    a.color === b.color &&
    a.hoist === b.hoist &&
    a.mentionable === b.mentionable &&
    a.perms === b.perms &&
    a.nx === b.nx &&
    a.pokesFrom === b.pokesFrom
  );
}

/** A value that changes whenever one of the edited fields of the live role does. */
export function roleSig(f: RoleFields): string {
  return [f.name, f.color, f.hoist, f.mentionable, f.perms, f.nx, f.pokesFrom].join('\u0000');
}

/** The draft rebased onto the live role (see the file comment). */
export function rebaseRole(draft: RoleFields, base: RoleFields, live: RoleFields): RoleFields {
  const pick = <K extends 'name' | 'color' | 'hoist' | 'mentionable' | 'pokesFrom'>(
    k: K,
  ): RoleFields[K] => (draft[k] === base[k] ? live[k] : draft[k]);
  return {
    name: pick('name'),
    color: pick('color'),
    hoist: pick('hoist'),
    mentionable: pick('mentionable'),
    perms: mergeBits(draft.perms, base.perms, live.perms),
    nx: mergeBits(draft.nx, base.nx, live.nx),
    pokesFrom: pick('pokesFrom'),
  };
}

/** The PATCH body: only the fields the draft changed from `base`. */
export function roleChanges(
  draft: RoleFields,
  base: RoleFields,
  everyone: boolean,
): {
  name?: string;
  color?: number;
  hoist?: boolean;
  mentionable?: boolean;
  permissions?: string;
  nx_permissions?: string;
  nx_pokes_from?: RolePokeRule;
} {
  return {
    ...(!everyone && draft.name.trim() !== base.name ? { name: draft.name.trim() } : {}),
    ...(!everyone && draft.color !== base.color ? { color: draft.color } : {}),
    ...(!everyone && draft.hoist !== base.hoist ? { hoist: draft.hoist } : {}),
    ...(!everyone && draft.mentionable !== base.mentionable
      ? { mentionable: draft.mentionable }
      : {}),
    ...(draft.perms !== base.perms ? { permissions: serializePermissions(draft.perms) } : {}),
    ...(draft.nx !== base.nx ? { nx_permissions: serializePermissions(draft.nx) } : {}),
    // @everyone has one too (the server's rule)
    ...(draft.pokesFrom !== base.pokesFrom ? { nx_pokes_from: draft.pokesFrom } : {}),
  };
}

/* ---------------------------------------------------------------- channels */

export interface ChannelFields {
  name: string;
  parent_id: Snowflake | null;
  topic: string;
  user_limit: number;
  bitrate: number;
  overwrites: Overwrite[];
  /** Slowmode in seconds, text channels (2026-10-29); absent is 0. */
  rate_limit_per_user?: number;
  /** A voice channel's region (2026-11-03); absent or null is Automatic. */
  rtc_region?: string | null;
}

const EMPTY = (id: Snowflake, type: 0 | 1): Overwrite => ({
  id,
  type,
  allow: '0',
  deny: '0',
  nx_allow: '0',
  nx_deny: '0',
});

export function sameOverwrite(a: Overwrite | undefined, b: Overwrite | undefined): boolean {
  if (!a || !b) return a === b;
  const n = (v: string | undefined) => parsePermissions(v ?? '0');
  return (
    a.id === b.id &&
    a.type === b.type &&
    n(a.allow) === n(b.allow) &&
    n(a.deny) === n(b.deny) &&
    n(a.nx_allow) === n(b.nx_allow) &&
    n(a.nx_deny) === n(b.nx_deny)
  );
}

function sameOverwrites(a: Overwrite[], b: Overwrite[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((o) =>
    sameOverwrite(
      o,
      b.find((x) => x.id === o.id),
    ),
  );
}

export function sameChannel(a: ChannelFields, b: ChannelFields): boolean {
  return (
    a.name === b.name &&
    a.parent_id === b.parent_id &&
    a.topic === b.topic &&
    a.user_limit === b.user_limit &&
    a.bitrate === b.bitrate &&
    (a.rate_limit_per_user ?? 0) === (b.rate_limit_per_user ?? 0) &&
    (a.rtc_region ?? null) === (b.rtc_region ?? null) &&
    sameOverwrites(a.overwrites, b.overwrites)
  );
}

export function channelSig(f: ChannelFields): string {
  const ows = [...f.overwrites]
    .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
    .map((o) => [o.id, o.type, o.allow, o.deny, o.nx_allow ?? '0', o.nx_deny ?? '0'].join(':'));
  return [
    f.name,
    f.parent_id ?? '',
    f.topic,
    f.user_limit,
    f.bitrate,
    f.rate_limit_per_user ?? 0,
    f.rtc_region ?? '',
    ...ows,
  ].join('\u0000');
}

/** One target's overwrite, three ways: what the draft changed wins, bit by bit. */
function mergeOverwrite(
  d: Overwrite | undefined,
  b: Overwrite | undefined,
  l: Overwrite | undefined,
): Overwrite | undefined {
  if (sameOverwrite(d, b)) return l; // untouched here: as it is live (also removed or added)
  if (!d) return undefined; // removed here: stays removed
  const base = b ?? EMPTY(d.id, d.type);
  const live = l ?? EMPTY(d.id, d.type);
  const pair = (ak: 'allow' | 'nx_allow', dk: 'deny' | 'nx_deny') => {
    const da = parsePermissions(d[ak] ?? '0');
    const dd = parsePermissions(d[dk] ?? '0');
    // a bit set to Allow, Inherit or Deny here keeps that state as a whole
    const touched =
      (da ^ parsePermissions(base[ak] ?? '0')) | (dd ^ parsePermissions(base[dk] ?? '0'));
    const allow = (da & touched) | (parsePermissions(live[ak] ?? '0') & ~touched);
    const deny = (dd & touched) | (parsePermissions(live[dk] ?? '0') & ~touched);
    return [serializePermissions(allow), serializePermissions(deny)] as const;
  };
  const [allow, deny] = pair('allow', 'deny');
  const [nx_allow, nx_deny] = pair('nx_allow', 'nx_deny');
  return { id: d.id, type: d.type, allow, deny, nx_allow, nx_deny };
}

export function mergeOverwrites(
  draft: Overwrite[],
  base: Overwrite[],
  live: Overwrite[],
): Overwrite[] {
  const ids = [
    ...new Set([...draft.map((o) => o.id), ...base.map((o) => o.id), ...live.map((o) => o.id)]),
  ];
  const by = (list: Overwrite[], id: Snowflake) => list.find((o) => o.id === id);
  return ids
    .map((id) => mergeOverwrite(by(draft, id), by(base, id), by(live, id)))
    .filter((o): o is Overwrite => !!o);
}

/** The draft rebased onto the live channel (see the file comment). */
export function rebaseChannel(
  draft: ChannelFields,
  base: ChannelFields,
  live: ChannelFields,
): ChannelFields {
  const pick = <K extends 'name' | 'parent_id' | 'topic' | 'user_limit' | 'bitrate'>(
    k: K,
  ): ChannelFields[K] => (draft[k] === base[k] ? live[k] : draft[k]);
  return {
    name: pick('name'),
    parent_id: pick('parent_id'),
    topic: pick('topic'),
    user_limit: pick('user_limit'),
    bitrate: pick('bitrate'),
    rate_limit_per_user:
      (draft.rate_limit_per_user ?? 0) === (base.rate_limit_per_user ?? 0)
        ? (live.rate_limit_per_user ?? 0)
        : (draft.rate_limit_per_user ?? 0),
    rtc_region:
      (draft.rtc_region ?? null) === (base.rtc_region ?? null)
        ? (live.rtc_region ?? null)
        : (draft.rtc_region ?? null),
    overwrites: mergeOverwrites(draft.overwrites, base.overwrites, live.overwrites),
  };
}

/** The overwrites to PUT (new or changed in the draft) and the targets to DELETE (removed in it). */
export function overwriteChanges(
  draft: Overwrite[],
  base: Overwrite[],
): { put: Overwrite[]; remove: Snowflake[] } {
  return {
    put: draft.filter(
      (o) =>
        !sameOverwrite(
          o,
          base.find((b) => b.id === o.id),
        ),
    ),
    remove: base.filter((b) => !draft.some((o) => o.id === b.id)).map((b) => b.id),
  };
}
